import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import {
  disableTwoFactor as defaultDisable,
  getTwoFactorStatus as defaultGetStatus,
  regenerateTwoFactorRecoveryCodes as defaultRegenerate,
  requestTwoFactorDisableEmailCode as defaultRequestDisableEmail,
  setupTwoFactor as defaultSetup,
  verifyEnableTwoFactor as defaultVerifyEnable,
  verifyTwoFactorDisableEmailCode as defaultVerifyDisableEmail,
  type TwoFactorSetupResponse,
  type TwoFactorStatusResponse,
} from '../api/twoFactorApi'

/**
 * 2FA TOTP UI hook (Phase 4 #44、設定画面用)。
 *
 * 設計判断:
 *   - status は mount 時に自動 load + reload で再取得可能
 *   - setup フローは startSetup → secret/QR 表示 → verifyEnable で完了
 *   - recovery codes は verify-enable / regenerate 成功時に **1 回限り** 平文を保持、
 *     UI で表示後 `clearRecoveryCodes()` を明示的に呼んで破棄するのが推奨
 *   - error 文言は i18n key を返す (UI 側で t() を当てる、CLAUDE.md §2 ルール 10)
 *   - deps DI で fetch を差し替え可能 (spec で使用)
 */

const ERROR_KEY_FROM_API = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'settings.2fa.error.invalid_code'
    if (err.status === 400) return 'settings.2fa.error.bad_request'
    if (err.status === 429) return 'settings.2fa.error.too_many_requests'

    return 'settings.2fa.error.network'
  }

  return 'settings.2fa.error.generic'
}

export interface UseTwoFactorDeps {
  readonly getStatus?: typeof defaultGetStatus
  readonly setup?: typeof defaultSetup
  readonly verifyEnable?: typeof defaultVerifyEnable
  readonly disable?: typeof defaultDisable
  readonly regenerate?: typeof defaultRegenerate
  readonly requestDisableEmailCode?: typeof defaultRequestDisableEmail
  readonly verifyDisableEmailCode?: typeof defaultVerifyDisableEmail
}

export interface UseTwoFactor {
  status: TwoFactorStatusResponse | null
  loading: boolean
  errorKey: string | null
  /** setup 中 (verify-enable 未完了) のみ非 null。startSetup / cancelSetup で操作 */
  setupData: TwoFactorSetupResponse | null
  /** verify-enable / regenerate 直後の平文 recovery codes (UI で 1 回限り表示) */
  recoveryCodes: readonly string[] | null
  isSubmitting: boolean
  reload: () => Promise<void>
  startSetup: () => Promise<void>
  cancelSetup: () => void
  /** 成功時 true、失敗時 false (errorKey に詳細セット) */
  verifyEnable: (code: string) => Promise<boolean>
  disable: (code: string) => Promise<boolean>
  /** Authenticator 紛失時の 2FA 解除フロー: 登録メールへ 6 桁 一時コード送信 (204) */
  requestDisableEmailCode: () => Promise<boolean>
  /** Authenticator 紛失時の 2FA 解除フロー: メール受信した 6 桁 code で 2FA 解除 */
  disableByEmailCode: (code: string) => Promise<boolean>
  regenerate: (code: string) => Promise<boolean>
  clearRecoveryCodes: () => void
}

export const useTwoFactor = (deps: UseTwoFactorDeps = {}): UseTwoFactor => {
  const getStatus = deps.getStatus ?? defaultGetStatus
  const setup = deps.setup ?? defaultSetup
  const verifyEnableFn = deps.verifyEnable ?? defaultVerifyEnable
  const disableFn = deps.disable ?? defaultDisable
  const regenerateFn = deps.regenerate ?? defaultRegenerate
  const requestDisableEmailFn = deps.requestDisableEmailCode ?? defaultRequestDisableEmail
  const verifyDisableEmailFn = deps.verifyDisableEmailCode ?? defaultVerifyDisableEmail

  const [status, setStatus] = useState<TwoFactorStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const mountedRef = useRef(true)

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true)
    setErrorKey(null)
    try {
      const next = await getStatus()
      if (!mountedRef.current) return
      setStatus(next)
    } catch (err: unknown) {
      if (!mountedRef.current) return
      setErrorKey(ERROR_KEY_FROM_API(err))
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [getStatus])

  useEffect(() => {
    mountedRef.current = true
    void reload()

    return (): void => {
      mountedRef.current = false
    }
  }, [reload])

  const startSetup = useCallback(async (): Promise<void> => {
    setIsSubmitting(true)
    setErrorKey(null)
    try {
      const res = await setup()
      if (!mountedRef.current) return
      setSetupData(res)
    } catch (err: unknown) {
      if (!mountedRef.current) return
      setErrorKey(ERROR_KEY_FROM_API(err))
    } finally {
      if (mountedRef.current) setIsSubmitting(false)
    }
  }, [setup])

  const cancelSetup = useCallback((): void => {
    setSetupData(null)
    setErrorKey(null)
  }, [])

  const verifyEnable = useCallback(
    async (code: string): Promise<boolean> => {
      setIsSubmitting(true)
      setErrorKey(null)
      try {
        const res = await verifyEnableFn(code)
        if (!mountedRef.current) return true
        setStatus({
          enabled: res.enabled,
          pending: res.pending,
          recoveryCodesRemaining: res.recoveryCodesRemaining,
        })
        setRecoveryCodes(res.recoveryCodes)
        setSetupData(null)

        return true
      } catch (err: unknown) {
        if (mountedRef.current) setErrorKey(ERROR_KEY_FROM_API(err))

        return false
      } finally {
        if (mountedRef.current) setIsSubmitting(false)
      }
    },
    [verifyEnableFn],
  )

  const disable = useCallback(
    async (code: string): Promise<boolean> => {
      setIsSubmitting(true)
      setErrorKey(null)
      try {
        const res = await disableFn(code)
        if (!mountedRef.current) return true
        setStatus(res)
        setRecoveryCodes(null)
        setSetupData(null)

        return true
      } catch (err: unknown) {
        if (mountedRef.current) setErrorKey(ERROR_KEY_FROM_API(err))

        return false
      } finally {
        if (mountedRef.current) setIsSubmitting(false)
      }
    },
    [disableFn],
  )

  const requestDisableEmailCode = useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true)
    setErrorKey(null)
    try {
      await requestDisableEmailFn()

      return true
    } catch (err: unknown) {
      if (mountedRef.current) setErrorKey(ERROR_KEY_FROM_API(err))

      return false
    } finally {
      if (mountedRef.current) setIsSubmitting(false)
    }
  }, [requestDisableEmailFn])

  const disableByEmailCode = useCallback(
    async (code: string): Promise<boolean> => {
      setIsSubmitting(true)
      setErrorKey(null)
      try {
        const res = await verifyDisableEmailFn(code)
        if (!mountedRef.current) return true
        setStatus(res)
        setRecoveryCodes(null)
        setSetupData(null)

        return true
      } catch (err: unknown) {
        if (mountedRef.current) setErrorKey(ERROR_KEY_FROM_API(err))

        return false
      } finally {
        if (mountedRef.current) setIsSubmitting(false)
      }
    },
    [verifyDisableEmailFn],
  )

  const regenerate = useCallback(
    async (code: string): Promise<boolean> => {
      setIsSubmitting(true)
      setErrorKey(null)
      try {
        const res = await regenerateFn(code)
        if (!mountedRef.current) return true
        setRecoveryCodes(res.codes)
        setStatus(prev =>
          prev === null ? null : { ...prev, recoveryCodesRemaining: res.codes.length },
        )

        return true
      } catch (err: unknown) {
        if (mountedRef.current) setErrorKey(ERROR_KEY_FROM_API(err))

        return false
      } finally {
        if (mountedRef.current) setIsSubmitting(false)
      }
    },
    [regenerateFn],
  )

  const clearRecoveryCodes = useCallback((): void => {
    setRecoveryCodes(null)
  }, [])

  return {
    status,
    loading,
    errorKey,
    setupData,
    recoveryCodes,
    isSubmitting,
    reload,
    startSetup,
    cancelSetup,
    verifyEnable,
    disable,
    requestDisableEmailCode,
    disableByEmailCode,
    regenerate,
    clearRecoveryCodes,
  }
}
